// ------------------------------------------------------------------
// Central GA4 event helper for BigLoads.
//
// Why this file exists:
//   Stock Sharetribe only fires `page_view`. We want a real funnel in
//   GA4 so we can measure what actually drives RENTAL INQUIRIES (the
//   north-star metric) and segment renter vs owner behavior.
//
//   gtag() is bootstrapped in public/index.html (early, in <head>) AND
//   by util/includeScripts.js for the hosted GA4 id (G-CDV0JGX4W8).
//   Either way window.gtag exists in the browser. This helper is the
//   ONE place all custom events go through, so it stays maintainable.
//
// Safe by construction:
//   - No-ops on the server (SSR) and if gtag isn't loaded yet.
//   - Never throws (wrapped in try/catch) so an analytics hiccup can
//     never break a real user action (e.g. sending an inquiry).
// ------------------------------------------------------------------

// Canonical event names (GA4 recommended names where one fits).
export const GA4_EVENTS = Object.freeze({
  // Primary conversion: a renter contacted an owner about a trailer.
  GENERATE_LEAD: 'generate_lead',
  // Funnel + engagement
  SIGN_UP: 'sign_up',
  SELECT_ROLE: 'select_role', // renter vs owner chosen in onboarding
  VIEW_LISTING: 'view_listing',
  BEGIN_INQUIRY: 'begin_inquiry', // opened/started the inquiry form
  CONTACT_OWNER: 'contact_owner', // Haul Star direct phone/email/website click
  HAUL_STAR_VIEW: 'haul_star_view', // viewed the subscription/upgrade page
  HAUL_STAR_BEGIN_CHECKOUT: 'haul_star_begin_checkout',
  SMS_OPT_IN: 'sms_opt_in',
  // Owner presell landing page (/list-your-trailer) split test.
  PRESELL_CTA_CLICK: 'presell_cta_click',
  // "What Trailer Do I Need?" quiz funnel (/what-trailer-do-i-need).
  QUIZ_START: 'quiz_start', // answered the first question
  QUIZ_STEP: 'quiz_step', // advanced to the next question
  QUIZ_TEASER: 'quiz_teaser', // got the AI teaser recommendation
  QUIZ_UNLOCK: 'quiz_unlock', // entered email + unlocked full results (conversion)
});

const isBrowser = () => typeof window !== 'undefined';

const hasGtag = () => isBrowser() && typeof window.gtag === 'function';

/**
 * Fire a GA4 event. Silent no-op if gtag isn't available.
 * @param {string} eventName
 * @param {object} params  Additional GA4 event params (snake_case).
 */
export const trackGA4Event = (eventName, params = {}) => {
  try {
    if (!eventName || !hasGtag()) {
      return;
    }
    // Clean out undefined/null values so GA4 doesn't get noise.
    const clean = Object.entries(params).reduce((acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        acc[k] = v;
      }
      return acc;
    }, {});
    window.gtag('event', eventName, clean);
  } catch (e) {
    // Analytics must never break a user flow.
    // eslint-disable-next-line no-console
    console.warn('[ga4] trackGA4Event failed:', e);
  }
};

/**
 * Set a GA4 user property (e.g. user_type = trailer-renter/trailer-owner).
 * Lets us segment the whole funnel by renter vs owner in GA4.
 * Safe no-op if gtag isn't available or the value is empty.
 * @param {object} properties  { user_type: 'trailer-owner', ... }
 */
export const setGA4UserProperties = (properties = {}) => {
  try {
    if (!hasGtag()) {
      return;
    }
    const clean = Object.entries(properties).reduce((acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        acc[k] = v;
      }
      return acc;
    }, {});
    if (Object.keys(clean).length === 0) {
      return;
    }
    window.gtag('set', 'user_properties', clean);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ga4] setGA4UserProperties failed:', e);
  }
};

/**
 * Convenience: derive the userType string from a currentUser entity and
 * push it as a GA4 user property. Called wherever we already have the user.
 */
export const trackUserTypeProperty = currentUser => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (userType) {
    setGA4UserProperties({ user_type: userType });
  }
};
